import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import "./ImageGrid.css";

/**
 * photos 与服务端详情一致：[{ type, imageUrl, videoUrl }]
 * 与 RN `ImageGrid.buildGalleryRows` 对齐
 */
function buildGalleryRows(images, photos) {
  if (Array.isArray(photos) && photos.length > 0) {
    return photos.map((p) => ({
      uri: p?.imageUrl ?? null,
      liveVideoUrl:
        p?.type === "live_photo" && p?.videoUrl ? p.videoUrl : null,
      isLivePhoto: p?.type === "live_photo",
    }));
  }
  return (images || []).map((uri) => ({
    uri: uri || null,
    liveVideoUrl: null,
    isLivePhoto: false,
  }));
}

/** Web：无 RN Android 限制，有 videoUrl 即展示实况入口（与 RN iOS 行为一致） */
const SUPPORT_LIVE_PHOTO_UI = true;

function LivePhotoClipBody({
  active,
  videoUri,
  posterUri,
  onClose,
  /** 大图预览内叠放：播完直接收起回到静图 */
  previewOverlay = false,
}) {
  const videoRef = useRef(null);
  const [clipEnded, setClipEnded] = useState(false);

  useEffect(() => {
    if (active && videoUri) {
      setClipEnded(false);
    }
  }, [active, videoUri]);

  useEffect(() => {
    if (!active || !videoUri || !videoRef.current) {
      return undefined;
    }
    if (!previewOverlay && clipEnded) {
      return undefined;
    }
    const v = videoRef.current;
    const run = async () => {
      try {
        v.currentTime = 0;
        await v.play();
      } catch (e) {
        console.warn("实况短片播放失败", e);
      }
    };
    run();
    return undefined;
  }, [active, videoUri, clipEnded, previewOverlay]);

  const handleEnded = useCallback(() => {
    if (previewOverlay) {
      onClose();
      return;
    }
    setClipEnded(true);
    if (videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
    }
  }, [previewOverlay, onClose]);

  const handleReplay = useCallback(
    (e) => {
      e.stopPropagation();
      setClipEnded(false);
      const v = videoRef.current;
      if (!v || !videoUri) return;
      try {
        v.currentTime = 0;
        void v.play();
      } catch (err) {
        console.warn("实况重播失败", err);
      }
    },
    [videoUri],
  );

  useEffect(() => {
    if (!active && videoRef.current) {
      try {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
    }
  }, [active]);

  if (!active || !videoUri) {
    return null;
  }

  const showPosterStill = !previewOverlay && clipEnded && !!posterUri;
  const showEndedPlaceholder = !previewOverlay && clipEnded && !posterUri;

  return (
    <div
      className={
        previewOverlay
          ? "live-clip-overlay-preview"
          : "live-clip-overlay-standalone"
      }
      onClick={!previewOverlay ? onClose : undefined}
    >
      <div
        className="live-clip-wrap"
        onClick={(e) => e.stopPropagation()}
        role="presentation"
      >
        {showPosterStill ? (
          <img
            src={posterUri}
            alt=""
            className="live-clip-media"
            draggable={false}
          />
        ) : showEndedPlaceholder ? (
          <div className="live-clip-media live-clip-ended-placeholder" />
        ) : (
          <video
            ref={videoRef}
            src={videoUri}
            poster={posterUri || undefined}
            className="live-clip-media"
            playsInline
            controls={false}
            onEnded={handleEnded}
          />
        )}
        {!previewOverlay && (
          <button
            type="button"
            className="live-clip-replay"
            onClick={handleReplay}
          >
            <span className="live-clip-replay-icon" aria-hidden>
              ◉
            </span>
            <span className="live-clip-replay-label">实况</span>
          </button>
        )}
      </div>
    </div>
  );
}

const ImageGrid = ({ images = [], photos, showMore = true }) => {
  const galleryRows = useMemo(
    () => buildGalleryRows(images, photos),
    [images, photos],
  );

  const previewUris = useMemo(
    () => galleryRows.map((r) => r.uri ?? null),
    [galleryRows],
  );

  const totalImageCount = galleryRows.length;
  const [isExpandAll, setIsExpandAll] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const previewScrollRef = useRef(null);
  const [scrollContainerWidth, setScrollContainerWidth] = useState(0);
  const [liveClip, setLiveClip] = useState(null);

  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const openLiveClip = useCallback((videoUri, posterUri) => {
    if (!SUPPORT_LIVE_PHOTO_UI || !videoUri) return;
    setLiveClip({ videoUri, posterUri: posterUri || null });
  }, []);

  /**
   * 从缩略图点「实况」：先进入大图预览并滚到当前张，再播实况，
   * 与在大图内点「实况」同一路径（previewOverlay + 相同画幅约束）。
   */
  const openLiveClipInPreview = useCallback(
    (videoUri, posterUri, index) => {
      if (!SUPPORT_LIVE_PHOTO_UI || !videoUri) return;
      if (!showMore) {
        setLiveClip({ videoUri, posterUri: posterUri || null });
        return;
      }
      if (!previewUris[index]) return;
      setCurrentPreviewIndex(index);
      setPreviewModalVisible(true);
      setLiveClip({ videoUri, posterUri: posterUri || null });
      setTimeout(() => {
        if (previewScrollRef.current) {
          const w = previewScrollRef.current.offsetWidth;
          if (w > 0) {
            previewScrollRef.current.scrollLeft = index * w;
          }
        }
      }, 100);
    },
    [showMore, previewUris],
  );

  const closeLiveClip = useCallback(() => {
    setLiveClip(null);
  }, []);

  const liveClipVisible = !!liveClip?.videoUri;

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
      if (previewScrollRef.current) {
        setScrollContainerWidth(previewScrollRef.current.offsetWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (previewModalVisible && previewScrollRef.current) {
      const timer = setTimeout(() => {
        setScrollContainerWidth(previewScrollRef.current.offsetWidth);
      }, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [previewModalVisible]);

  const SCREEN_WIDTH = screenSize.width;
  const IMAGE_GAP = 4;
  const MAX_VISIBLE_IMAGES = 9;

  const visibleRows = (() => {
    if (isExpandAll) {
      return galleryRows;
    }
    return totalImageCount > MAX_VISIBLE_IMAGES
      ? galleryRows.slice(0, 8)
      : galleryRows;
  })();

  const getColumnCount = () => {
    const imgCount = isExpandAll
      ? totalImageCount
      : Math.min(totalImageCount, MAX_VISIBLE_IMAGES);
    if (imgCount === 1) return 1;
    if (imgCount === 2) return 2;
    return 3;
  };

  const getImageWidth = () => {
    const columnCount = getColumnCount();
    const totalGap = (columnCount - 1) * IMAGE_GAP;
    const totalAvailableWidth = SCREEN_WIDTH - 36 - totalGap;
    return totalAvailableWidth / columnCount;
  };

  const hasMoreImages = totalImageCount > MAX_VISIBLE_IMAGES;
  const remainingImageCount = totalImageCount - 8;

  const handleMoreImagePress = () => {
    if (showMore && hasMoreImages) {
      setIsExpandAll(true);
    }
  };

  const handleImagePreview = (index) => {
    if (!showMore) return;
    if (!previewUris[index]) return;

    setCurrentPreviewIndex(index);
    setPreviewModalVisible(true);
    setTimeout(() => {
      if (previewScrollRef.current) {
        const w =
          scrollContainerWidth > 0
            ? scrollContainerWidth
            : previewScrollRef.current.offsetWidth;
        if (w > 0) {
          previewScrollRef.current.scrollLeft = index * w;
        }
      }
    }, 100);
  };

  const closeImagePreview = (e) => {
    if (e) e.stopPropagation();
    setLiveClip(null);
    setPreviewModalVisible(false);
  };

  const handlePreviewScroll = (e) => {
    requestAnimationFrame(() => {
      if (!previewScrollRef.current) return;
      const containerWidth =
        previewScrollRef.current.offsetWidth || scrollContainerWidth;
      if (containerWidth === 0) return;
      const index = Math.round(e.target.scrollLeft / containerWidth);
      const validIndex = Math.max(
        0,
        Math.min(index, previewUris.length - 1),
      );
      setCurrentPreviewIndex(validIndex);
    });
  };

  const handleMorePlaceholderPress = () => {
    if (!showMore || !hasMoreImages) return;
    setIsExpandAll(true);
    const targetIndex = 8;
    setCurrentPreviewIndex(targetIndex);
    setPreviewModalVisible(true);
    setTimeout(() => {
      if (previewScrollRef.current) {
        const w =
          scrollContainerWidth > 0
            ? scrollContainerWidth
            : previewScrollRef.current.offsetWidth;
        if (w > 0) {
          previewScrollRef.current.scrollLeft = targetIndex * w;
        }
      }
    }, 100);
  };

  const columnCount = getColumnCount();
  const imageWidth = getImageWidth();

  return (
    <>
      <div className="image-grid-container">
        {visibleRows.map((row, idx) => {
          const img = row.uri;
          return (
            <div
              key={`grid-cell-${idx}`}
              role={showMore && img ? "button" : undefined}
              tabIndex={showMore && img ? 0 : undefined}
              onClick={() => img && handleImagePreview(idx)}
              onKeyDown={(e) => {
                if (
                  showMore &&
                  img &&
                  (e.key === "Enter" || e.key === " ")
                ) {
                  e.preventDefault();
                  handleImagePreview(idx);
                }
              }}
              className={`image-item ${!img ? "image-item-placeholder" : ""}`}
              style={{
                width: imageWidth,
                height: imageWidth,
                marginRight:
                  idx % columnCount !== columnCount - 1 ? `${IMAGE_GAP}px` : 0,
                marginBottom:
                  idx < visibleRows.length - columnCount
                    ? `${IMAGE_GAP}px`
                    : 0,
                cursor: showMore && img ? "pointer" : "default",
              }}
            >
              {img ? (
                <div className="photo-cell-inner">
                  <img src={img} alt={`image-${idx}`} />
                  {SUPPORT_LIVE_PHOTO_UI && !!row.liveVideoUrl ? (
                    <button
                      type="button"
                      className="live-photo-marker live-photo-marker-grid"
                      onClick={(e) => {
                        e.stopPropagation();
                        openLiveClipInPreview(row.liveVideoUrl, img, idx);
                      }}
                    >
                      <span className="live-photo-icon" aria-hidden>
                        ◉
                      </span>
                      <span className="live-photo-marker-label-small">
                        实况
                      </span>
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          );
        })}

        {hasMoreImages && !isExpandAll && previewUris[8] && (
          <div
            className="image-item image-item-more"
            style={{
              width: imageWidth,
              height: imageWidth,
              marginRight:
                8 % columnCount !== columnCount - 1 ? `${IMAGE_GAP}px` : 0,
              marginBottom:
                8 < visibleRows.length - columnCount ? `${IMAGE_GAP}px` : 0,
              position: "relative",
              cursor: showMore ? "pointer" : "default",
            }}
            role={showMore ? "button" : undefined}
            tabIndex={showMore ? 0 : undefined}
            onClick={() => {
              if (showMore) {
                handleMorePlaceholderPress();
              }
            }}
            onKeyDown={(e) => {
              if (
                showMore &&
                (e.key === "Enter" || e.key === " ") &&
                hasMoreImages
              ) {
                e.preventDefault();
                handleMorePlaceholderPress();
              }
            }}
          >
            <img src={previewUris[8]} alt="more" />
            <div className="more-images-overlay">
              <span className="more-images-count">+{remainingImageCount}</span>
            </div>
          </div>
        )}
      </div>

      {previewModalVisible && (
        <div className="preview-modal">
          <div className="preview-header">
            <div className="preview-counter">
              {currentPreviewIndex + 1} / {galleryRows.length}
            </div>
            <button
              type="button"
              onClick={closeImagePreview}
              className="preview-close-btn"
            >
              ×
            </button>
          </div>

          <div className="preview-content">
            <div
              ref={previewScrollRef}
              className="preview-scroll-container"
              onScroll={handlePreviewScroll}
            >
              {galleryRows.map((row, index) => {
                const uri = row.uri;
                return (
                  <div
                    key={`preview-${index}`}
                    className="preview-slide"
                    role="presentation"
                  >
                    {uri ? (
                      <div className="preview-slide-inner">
                        <img src={uri} alt={`preview-${index}`} />
                        {SUPPORT_LIVE_PHOTO_UI && !!row.liveVideoUrl ? (
                          <button
                            type="button"
                            className="live-photo-marker live-photo-marker-fullscreen"
                            onClick={(e) => {
                              e.stopPropagation();
                              openLiveClip(row.liveVideoUrl, uri);
                            }}
                          >
                            <span className="live-photo-icon" aria-hidden>
                              ◉
                            </span>
                            <span className="live-photo-marker-label">
                              实况
                            </span>
                          </button>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {liveClipVisible && SUPPORT_LIVE_PHOTO_UI && liveClip && (
              <LivePhotoClipBody
                active
                videoUri={liveClip.videoUri}
                posterUri={liveClip.posterUri}
                onClose={closeLiveClip}
                previewOverlay
              />
            )}
          </div>
        </div>
      )}

      {liveClipVisible && !previewModalVisible && SUPPORT_LIVE_PHOTO_UI && liveClip && (
        <LivePhotoClipBody
          active
          videoUri={liveClip.videoUri}
          posterUri={liveClip.posterUri}
          onClose={closeLiveClip}
          previewOverlay={false}
        />
      )}
    </>
  );
};

export default ImageGrid;
